import React from 'react';
import {
	type FieldPath,
	type FieldValues,
	type PathValue,
} from 'react-hook-form';
import type {SetRequired} from 'type-fest';
import {
	DateField,
	type DateFieldProps,
	type DateValue,
} from '@netx/core-components/DateField';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {splitEventHandler} from '@netx/core-next/utils';
import {combineRefs} from '@netx/core-next/utils/react';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '../ReactHookFormControl';

export interface ReactHookFormDateFieldProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
		SetRequired<DateFieldProps, 'name'>,
		TFieldValues,
		TName
	> {
	/** hook for parsing the value from the form to the input */
	parseValueIn?: (value: PathValue<TFieldValues, TName>) => string;
	/** hook for parsing the value from the input to the form */
	parseValueOut?: (value: DateValue) => any;
}

export const ReactHookFormDateFieldPrimitive = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		ControllerProps,
		parseValueIn,
		parseValueOut,
		...props
	}: ReactHookFormDateFieldProps<TFieldValues, TName>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={props.name as TName}
			render={({field, formState}) => {
				return (
					<DateField
						error={formState.errors[props.name]?.message?.toString()}
						{...props}
						{...field}
						disabled={props.disabled || field.disabled}
						ref={ref}
						inputRef={combineRefs(props.inputRef, field.ref)}
						value={parseValueIn ? parseValueIn(field.value) : field.value ?? ''}
						onChange={useConstCallback(
							splitEventHandler(props.onChange, (event, value) =>
								field.onChange(parseValueOut ? parseValueOut(value) : value),
							),
						)}
					/>
				);
			}}
			rules={{
				required: props.required,
				...ControllerProps?.rules,
			}}
		/>
	);
};

/**
 * `react-hook-form` controlled `DateField` component
 */
export const ReactHookFormDateField = React.forwardRef(
	ReactHookFormDateFieldPrimitive,
) as <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormDateFieldProps<TFieldValues, TName>,
) => JSX.Element;

export default ReactHookFormDateField;
